import { calculateCogsInEUR, groupCogsByCurrency } from '@/lib/cogs';
import { formatPrice } from '@/lib/helpers';
import {
  IServiceBlock,
  getServiceBlockTiers,
} from '@/models/commercial-proposals/blocks.store';
import { useIncludedAgentServicesMulti } from '@/models/services';
import { useRates } from '@/models/rates';
import { Loader, Text } from '@mantine/core';
import EmptyCell from '../../cells/EmptyCell/EmptyCell';
import ServiceBaseRow from '../ServiceBaseRow/ServiceBaseRow';

const calculateSummaryPrice = (block: IServiceBlock, tier: number) => {
  return block.services
    .filter((s) => s.config[tier]?.included)
    .reduce((acc, parent) => acc + parent.price.amount, 0);
};

function TierCogsSummary({
  block,
  tier,
}: {
  block: IServiceBlock;
  tier: number;
}) {
  const includedServiceIds = block.services
    .filter((s) => !s.parentId && s.config[tier]?.included)
    .map((s) => s.serviceId);

  const { data: rates } = useRates();
  const queries = useIncludedAgentServicesMulti(includedServiceIds);

  const isLoading = queries.some((q) => q.isLoading);

  let cogsInEUR: number | null = null;
  if (rates && !isLoading) {
    const merged: Record<string, number> = {};
    for (const q of queries) {
      if (!q.data) continue;
      const grouped = groupCogsByCurrency(q.data);
      for (const [cur, amount] of Object.entries(grouped)) {
        merged[cur] = (merged[cur] || 0) + amount;
      }
    }
    cogsInEUR = calculateCogsInEUR(merged, rates);
  }

  if (isLoading) return <Loader size={12} />;
  if (!cogsInEUR) return null;

  return (
    <Text size="xs" c="dimmed">
      COGS: {formatPrice(cogsInEUR, 'EUR')}
    </Text>
  );
}

export default function ServiceSummaryRow({
  currency,
  block,
}: {
  currency: string;
  block: IServiceBlock;
}) {
  const tiers = getServiceBlockTiers(block.type);

  return (
    <ServiceBaseRow
      style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}
    >
      <EmptyCell />
      <EmptyCell />
      {Array.from({ length: tiers }).map((_, tier) => (
        <div key={tier} style={{ padding: 5 }}>
          <Text size="xs" fw={500}>
            {formatPrice(calculateSummaryPrice(block, tier), currency)}
          </Text>
          <TierCogsSummary block={block} tier={tier} />
        </div>
      ))}
    </ServiceBaseRow>
  );
}
